import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { RefreshCw, ArrowRight, MessageSquare, UserPlus, ArrowRightLeft, AlertTriangle } from "lucide-react";

const PIPELINE = [
  "received","review","proposal","quotation","accepted",
  "planning","field_verification","evidence_review","report_issued","completed",
];
const PIPELINE_LABEL = {
  received: "Request Received", review: "Initial Review", proposal: "Scope Confirmation",
  quotation: "Proposal / Quotation", accepted: "Client Acceptance", planning: "Planning",
  field_verification: "Field Verification", evidence_review: "Evidence Review",
  report_issued: "Report Preparation", completed: "Completed",
};
const URGENCY_TONE = { normal: "bg-white/10 text-white/70", priority: "bg-amber-500/20 text-amber-300", critical: "bg-red-500/20 text-red-300" };

export default function AdminVerificationRequests() {
  const [searchParams] = useSearchParams();
  const initialCountry = searchParams.get("country") || "";
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ status: "any", country: initialCountry, urgency: "any", needs_info: "any", source: "any", q: "" });
  const [selected, setSelected] = useState(null);
  const [drawer, setDrawer] = useState({ note: "", team: "", advanceTo: "", reason: "" });

  const load = async (override) => {
    setBusy(true);
    const src = override || filters;
    try {
      const params = {};
      if (src.status && src.status !== "any") params.status = src.status;
      if (src.country) params.country = src.country;
      if (src.urgency && src.urgency !== "any") params.urgency = src.urgency;
      if (src.needs_info === "yes") params.needs_info = true;
      if (src.needs_info === "no") params.needs_info = false;
      if (src.source && src.source !== "any") params.source = src.source;
      if (src.q) params.q = src.q;
      const { data } = await api.get("/verification-requests/admin", { params });
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setBusy(false); }
  };
  // On mount and whenever the ?country= URL param changes, refetch.
  useEffect(() => { load({ ...filters, country: initialCountry }); setFilters(f => ({ ...f, country: initialCountry })); /* eslint-disable-next-line */ }, [initialCountry]);

  const openRow = (r) => { setSelected(r); setDrawer({ note: "", team: r.assigned_team || "", advanceTo: "", reason: "" }); };

  const advance = async () => {
    if (!selected || !drawer.advanceTo) return;
    try {
      await api.post(`/verification-requests/${selected.reference}/advance`, { status: drawer.advanceTo, reason: drawer.reason });
      toast.success(`${selected.reference} → ${PIPELINE_LABEL[drawer.advanceTo]}`);
      const { data } = await api.get(`/verification-requests/${selected.reference}`);
      setSelected(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addNote = async (needsInfo = false) => {
    if (!selected || !drawer.note.trim()) return;
    try {
      await api.post(`/verification-requests/${selected.reference}/notes`, { text: drawer.note, needs_customer_info: needsInfo });
      toast.success("Note added");
      const { data } = await api.get(`/verification-requests/${selected.reference}`);
      setSelected(data);
      setDrawer({ ...drawer, note: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const assign = async () => {
    if (!selected) return;
    try {
      await api.post(`/verification-requests/${selected.reference}/assign`, { assigned_team: drawer.team, reason: "Assigned from triage" });
      toast.success("Team assigned");
      const { data } = await api.get(`/verification-requests/${selected.reference}`);
      setSelected(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1500px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="kicker">Executive · Continental Requests</div>
            <h1 className="display text-3xl">Verification Request Triage{initialCountry ? ` · ${initialCountry}` : ""}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {initialCountry
                ? <>Country-scoped operational workspace for <span className="mono uppercase tracking-widest text-primary">{initialCountry}</span>. Requests · escrow-linked value · priority alerts.</>
                : "Every TV-AFR submission from every African country. Filter, assign, advance and note."}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{count} total</span>
            <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="triage-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
            <Link to="/ops"><Button size="sm" variant="outline">Ops Centre</Button></Link>
          </div>
        </div>

        {/* Filters */}
        <div className="grid md:grid-cols-6 gap-2 mb-4">
          <Input placeholder="Search reference / org / contact" value={filters.q} onChange={(e) => setFilters({...filters, q: e.target.value})} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="triage-search" />
          <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
            <SelectTrigger data-testid="triage-status"><SelectValue placeholder="Stage" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All stages</SelectItem>
              {PIPELINE.map(s => <SelectItem key={s} value={s}>{PIPELINE_LABEL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Country" value={filters.country} onChange={(e) => setFilters({...filters, country: e.target.value})} data-testid="triage-country" />
          <Select value={filters.urgency} onValueChange={(v) => setFilters({...filters, urgency: v})}>
            <SelectTrigger data-testid="triage-urgency"><SelectValue placeholder="Urgency" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any urgency</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="priority">Priority</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.needs_info} onValueChange={(v) => setFilters({...filters, needs_info: v})}>
            <SelectTrigger data-testid="triage-needs-info"><SelectValue placeholder="Needs info?" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="yes">Awaiting customer</SelectItem>
              <SelectItem value="no">Not blocked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.source} onValueChange={(v) => setFilters({...filters, source: v})}>
            <SelectTrigger data-testid="triage-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any source</SelectItem>
              <SelectItem value="country_portal">Country portal</SelectItem>
              <SelectItem value="enterprise">Enterprise enquiry</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mb-4 flex gap-2">
          <Button size="sm" onClick={load}>Apply filters</Button>
          <Button size="sm" variant="ghost" onClick={() => { setFilters({ status: "any", country: "", urgency: "any", needs_info: "any", source: "any", q: "" }); setTimeout(load, 10); }}>Reset</Button>
        </div>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
          {/* Rows */}
          <div className="border border-border bg-card overflow-hidden">
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-border">
              {items.length === 0 && !busy && (
                <div className="p-8 text-center text-sm text-muted-foreground">No requests match the current filters.</div>
              )}
              {items.map((r) => (
                <button
                  key={r.reference}
                  onClick={() => openRow(r)}
                  data-testid={`triage-row-${r.reference}`}
                  className={`w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors ${selected?.reference === r.reference ? "bg-primary/5 border-l-2 border-l-primary" : ""}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="mono text-[11px] uppercase tracking-widest text-primary">{r.reference}</div>
                      <div className="text-sm font-medium mt-0.5">{r.organisation || r.contact_name || "—"}</div>
                      <div className="text-[11.5px] text-muted-foreground">{r.country} · {r.category || r.service_required || "—"}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${URGENCY_TONE[r.urgency] || URGENCY_TONE.normal}`}>{r.urgency}</span>
                      <span className="mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary">{PIPELINE_LABEL[r.status] || r.status}</span>
                      {r.needs_info && <span className="mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Info</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail drawer */}
          <div className="border border-border bg-card p-5 sticky top-24 self-start max-h-[80vh] overflow-y-auto">
            {!selected && <div className="text-sm text-muted-foreground">Select a row to view request details.</div>}
            {selected && (
              <div data-testid="triage-detail">
                <div className="mono text-[11px] uppercase tracking-widest text-primary">{selected.reference}</div>
                <h3 className="text-lg font-semibold mt-1">{selected.organisation || selected.contact_name}</h3>
                <div className="mono text-[11px] text-muted-foreground mt-0.5">{selected.country} · {selected.city || "—"} · {selected.category || selected.service_required || "—"}</div>

                {/* Contact + meta */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 text-[12.5px]">
                  <M l="Contact" v={selected.contact_name} />
                  <M l="Email"   v={selected.email} />
                  <M l="Phone"   v={selected.telephone || "—"} />
                  <M l="Value"   v={selected.estimated_value_usd ? `$${Number(selected.estimated_value_usd).toLocaleString()}` : "—"} />
                  <M l="Source"  v={selected.source || "country_portal"} />
                  <M l="Team"    v={selected.assigned_team || "Unassigned"} />
                </div>

                <div className="mt-4">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Description</div>
                  <p className="text-[12.5px] mt-1 whitespace-pre-wrap">{selected.description}</p>
                </div>

                {/* Advance stage */}
                <div className="mt-5 border-t border-border pt-4">
                  <div className="text-xs font-semibold mb-2 flex items-center gap-1.5"><ArrowRightLeft className="h-3.5 w-3.5" /> Advance stage</div>
                  <div className="flex gap-2">
                    <Select value={drawer.advanceTo} onValueChange={(v) => setDrawer({ ...drawer, advanceTo: v })}>
                      <SelectTrigger className="text-xs" data-testid="triage-advance-select"><SelectValue placeholder="Select new stage" /></SelectTrigger>
                      <SelectContent>{PIPELINE.map(s => <SelectItem key={s} value={s}>{PIPELINE_LABEL[s]}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" onClick={advance} disabled={!drawer.advanceTo} data-testid="triage-advance-btn"><ArrowRight className="h-3.5 w-3.5" /></Button>
                  </div>
                  <Input placeholder="Reason (optional)" value={drawer.reason} onChange={(e) => setDrawer({ ...drawer, reason: e.target.value })} className="mt-2 text-xs h-8" data-testid="triage-advance-reason" />
                </div>

                {/* Assign team */}
                <div className="mt-4 border-t border-border pt-4">
                  <div className="text-xs font-semibold mb-2 flex items-center gap-1.5"><UserPlus className="h-3.5 w-3.5" /> Assign team</div>
                  <div className="flex gap-2">
                    <Input placeholder="Team name (e.g. ZA-Ops)" value={drawer.team} onChange={(e) => setDrawer({ ...drawer, team: e.target.value })} className="text-xs h-8" data-testid="triage-team-input" />
                    <Button size="sm" onClick={assign} data-testid="triage-assign-btn">Assign</Button>
                  </div>
                </div>

                {/* Add note */}
                <div className="mt-4 border-t border-border pt-4">
                  <div className="text-xs font-semibold mb-2 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Internal note</div>
                  <textarea rows={2} value={drawer.note} onChange={(e) => setDrawer({ ...drawer, note: e.target.value })} placeholder="Note (internal only — never shown to customer)" className="w-full text-xs border border-border rounded-sm p-2 bg-background" data-testid="triage-note-input" />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={() => addNote(false)} disabled={!drawer.note.trim()} data-testid="triage-note-add">Add note</Button>
                    <Button size="sm" variant="outline" onClick={() => addNote(true)} disabled={!drawer.note.trim()} data-testid="triage-note-needs-info">Add + Mark "needs customer info"</Button>
                  </div>
                </div>

                {/* Pipeline history */}
                <div className="mt-5 border-t border-border pt-4">
                  <div className="text-xs font-semibold mb-2">Pipeline history ({selected.pipeline?.length || 0})</div>
                  <ol className="space-y-1.5 max-h-[240px] overflow-y-auto">
                    {(selected.pipeline || []).slice().reverse().map((p, i) => (
                      <li key={i} className="text-[11.5px] border-l-2 border-primary/40 pl-2">
                        <div className="mono uppercase tracking-widest text-[9.5px] text-primary">{PIPELINE_LABEL[p.status] || p.status}</div>
                        <div className="text-muted-foreground">{fmt(p.at)} · by {p.by || "system"}</div>
                        {p.note && <div className="text-[11.5px] mt-0.5">{p.note}</div>}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Internal notes */}
                {(selected.internal_notes || []).length > 0 && (
                  <div className="mt-5 border-t border-border pt-4">
                    <div className="text-xs font-semibold mb-2">Internal notes ({selected.internal_notes.length})</div>
                    <ol className="space-y-1.5 max-h-[240px] overflow-y-auto">
                      {selected.internal_notes.slice().reverse().map((n, i) => (
                        <li key={i} className="text-[11.5px] border-l-2 border-amber-500/40 pl-2">
                          <div className="text-muted-foreground text-[9.5px] mono uppercase tracking-widest">{fmt(n.at)} · {n.by || "?"}</div>
                          <div className="text-[11.5px]">{n.text}</div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Supporting docs */}
                {(selected.supporting_documents || []).length > 0 && (
                  <div className="mt-5 border-t border-border pt-4">
                    <div className="text-xs font-semibold mb-2">Supporting documents ({selected.supporting_documents.length})</div>
                    <ul className="space-y-1 text-[11.5px]">
                      {selected.supporting_documents.map((d, i) => (
                        <li key={i} className="flex items-center justify-between border border-border rounded-sm p-2">
                          <div>
                            <div className="text-[12.5px]">{d.filename || d.file_id}</div>
                            <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{d.category || "—"}</div>
                          </div>
                          <span className="mono text-[9.5px] text-muted-foreground">{d.file_hash?.slice(0, 8) || ""}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const M = ({ l, v }) => (<div><div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{l}</div><div className="mt-0.5">{v}</div></div>);
const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };
